import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HTTPError } from "ky";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared/error-state";
import { SmartCreationResultView } from "@/components/smart-creation/SmartCreationResultView";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useCampaignCreationTaskScope } from "@/hooks/use-campaign-creation-tasks";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  CAMPAIGN_CREATION_TASK_QUERY_KEY,
  campaignCreationTaskKey,
  fetchCampaignCreationTask,
  reconcileCampaignCreationTask,
  retryCampaignCreationTask,
} from "@/lib/campaign-creation-tasks";
import { SSE_FALLBACK_POLL_MS } from "@/lib/sse-invalidation";
import { useAuthStore } from "@/stores/auth";

interface SmartCreationResultProps {
  taskId: string;
  shopId?: string;
  canMutate: boolean;
  onClose(): void;
  onResumeDraft?(): void;
}

export function SmartCreationResult({
  taskId,
  shopId,
  canMutate,
  onClose,
  onResumeDraft,
}: SmartCreationResultProps) {
  const { t } = useTranslation("smartCreation");
  const queryClient = useQueryClient();
  const scope = useCampaignCreationTaskScope();
  const shops = useAuthStore((state) => state.shops);
  const setCurrentShop = useAuthStore((state) => state.setCurrentShop);
  const queryKey = campaignCreationTaskKey(scope, shopId ?? null, taskId);
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchCampaignCreationTask(shopId, taskId, signal),
    enabled: Boolean(scope),
    staleTime: 15_000,
    retry: false,
    // The AppLayout stream is shared across pages; polling is a fallback for lost hints.
    refetchInterval: (query) =>
      ["queued", "running"].includes(query.state.data?.status ?? "") ? SSE_FALLBACK_POLL_MS : false,
  });
  const task = query.data;
  const accessDenied =
    query.error instanceof HTTPError && [401, 403].includes(query.error.response.status);
  const visible = Boolean(
    scope &&
      !accessDenied &&
      task &&
      shops.some((shop) => shop.shopId === task.shopId && shop.workspaceId === scope.workspaceId),
  );

  useEffect(() => {
    if (task && visible) setCurrentShop(task.shopId);
  }, [task, visible, setCurrentShop]);

  const action = useMutation({
    mutationFn: async ({ kind, nodeKey }: { kind: "retry" | "reconcile"; nodeKey?: string }) => {
      if (
        !task ||
        !visible ||
        query.isError ||
        query.isFetching ||
        !canMutate ||
        !task.allowedActions.includes(kind)
      ) {
        throw new Error(t("errors.action"));
      }
      const input = { expectedRevision: task.revision };
      if (kind === "retry") return retryCampaignCreationTask(task.shopId, task.id, input);
      if (!nodeKey) throw new Error(t("errors.action"));
      return reconcileCampaignCreationTask(task.shopId, task.id, { ...input, nodeKey });
    },
    onSuccess: (next) => {
      queryClient.setQueryData(queryKey, next);
      void queryClient.invalidateQueries({ queryKey: CAMPAIGN_CREATION_TASK_QUERY_KEY });
    },
    onError: async (error) => {
      toast.error(await getApiErrorMessage(error));
      void query.refetch();
    },
  });

  return (
    <section className="flex min-h-0 flex-1 flex-col" data-smart-creation-flow>
      <header className="flex min-h-[88px] shrink-0 items-center justify-between border-b border-line px-6 py-4">
        <h1 className="text-xl font-medium">{t("header.title")}</h1>
        <Button variant="outline" size="sm" onClick={onClose}>
          {t("actions.close")}
        </Button>
      </header>
      {query.isLoading ? (
        <div className="space-y-3 p-6">
          <Skeleton className="h-6 w-72" />
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
      ) : !task || !visible ? (
        <div className="space-y-4 p-6">
          <ErrorState message={t("task.loadError")} onRetry={() => void query.refetch()} />
          {onResumeDraft && (
            <Button variant="outline" onClick={onResumeDraft}>
              {t("task.resumeDraft")}
            </Button>
          )}
        </div>
      ) : (
        <SmartCreationResultView
          task={task}
          canMutate={canMutate && !query.isError && !query.isFetching}
          stale={query.isError}
          pending={action.isPending}
          onRetry={() => action.mutate({ kind: "retry" })}
          onReconcile={(nodeKey) => action.mutate({ kind: "reconcile", nodeKey })}
          onRefresh={() => void query.refetch()}
        />
      )}
    </section>
  );
}
